/**
 * Google Analytics 4 Service for Web Application
 * 
 * Simplified service that uses the Google tag (gtag.js) loaded via HTML snippet.
 * The Google tag is initialized in the HTML head section of each page.
 */

// Import root config file for environment detection
// @ts-ignore - Root config file is outside src
import rootConfig from '@root/google-analytics.env.js';

// Get the selected environment and config
const config = (rootConfig as any)?.default || rootConfig;
const currentEnv = config?.environment || 'localhost';

// Type definitions for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Check if GA4 is loaded and ready
 */
function isGA4Ready(): boolean {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function' && 
         typeof window.dataLayer !== 'undefined';
}

/**
 * Check if we should skip tracking (localhost environment)
 */
function shouldSkipTracking(): boolean {
  return currentEnv === 'localhost';
}

/**
 * Track a GA4 event
 * @param eventName - Name of the event
 * @param params - Event parameters
 */
export function trackEvent(eventName: string, params?: any): void {
  // Skip if localhost
  if (shouldSkipTracking()) {
    if (typeof window !== 'undefined') {
      console.log('[GA4] Event (localhost, not sent):', eventName, params);
    }
    return;
  }

  // Validate parameters
  if (!eventName || typeof eventName !== 'string') {
    console.error('[GA4] Invalid event name:', eventName);
    return;
  }

  if (params !== undefined && (typeof params !== 'object' || params === null || Array.isArray(params))) {
    console.error('[GA4] Invalid event parameters (must be object):', params);
    return;
  }

  // Check if gtag is available
  if (!isGA4Ready()) {
    console.warn('[GA4] gtag not available. Event not sent:', eventName);
    return;
  }

  try {
    window.gtag('event', eventName, params || {});
    
    if (currentEnv === 'localhost') {
      console.log('[GA4] Event sent:', eventName, params);
    }
  } catch (error) {
    console.error('[GA4] Error sending event:', error);
  }
}

/**
 * Track a page view
 * @param pageTitle - Title of the page
 * @param pagePath - Path of the page (optional)
 */
export function trackPageView(pageTitle: string, pagePath?: string): void {
  trackEvent('page_view', {
    page_title: pageTitle,
    page_location: pagePath || (typeof window !== 'undefined' ? window.location.href : ''),
    page_path: pagePath || (typeof window !== 'undefined' ? window.location.pathname : ''),
  });
}

/**
 * Set user ID globally
 * @param userId - Firebase user ID
 */
export function setUserId(userId: string): void {
  if (shouldSkipTracking()) {
    console.log('[GA4] Set user ID (localhost, not sent):', userId);
    return;
  }

  if (!userId || typeof userId !== 'string') {
    console.error('[GA4] Invalid user ID:', userId);
    return;
  }

  if (!isGA4Ready()) {
    console.warn('[GA4] gtag not available. User ID not set.');
    return;
  }

  try {
    window.gtag('set', { user_id: userId });
    
    if (currentEnv === 'localhost') {
      console.log('[GA4] User ID set:', userId);
    }
  } catch (error) {
    console.error('[GA4] Error setting user ID:', error);
  }
}

/**
 * Set user properties
 * @param properties - User properties object
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (shouldSkipTracking()) {
    console.log('[GA4] Set user properties (localhost, not sent):', properties);
    return;
  }

  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    console.error('[GA4] Invalid user properties:', properties);
    return;
  }

  if (!isGA4Ready()) {
    console.warn('[GA4] gtag not available. User properties not set.');
    return;
  }

  try {
    window.gtag('set', { user_properties: properties });
    
    if (currentEnv === 'localhost') {
      console.log('[GA4] User properties set:', properties);
    }
  } catch (error) {
    console.error('[GA4] Error setting user properties:', error);
  }
}

/**
 * Clear user ID (on logout)
 */
export function clearUserId(): void {
  if (shouldSkipTracking()) {
    console.log('[GA4] Clear user ID (localhost)');
    return;
  }

  if (!isGA4Ready()) {
    console.warn('[GA4] gtag not available. User ID not cleared.');
    return;
  }

  try {
    window.gtag('set', { user_id: null });
  } catch (error) {
    console.error('[GA4] Error clearing user ID:', error);
  }
}
